import { useHead } from "@unhead/vue";
import { type Ref } from "vue";
import type { RouteLocationNormalizedLoaded } from "vue-router";
import type { RouteNamedMap } from "vue-router/auto-routes";

const images = import.meta.glob<string>("../assets/images/social/*.jpg", {
  eager: true,
  query: "?url&no-inline",
  import: "default",
});

const routeImages: Partial<Record<keyof RouteNamedMap, string>> = {
  "home-locale": "homepage",
  "product-locale": "product",
  "partners-locale": "partner",
  "partners-trygg-hansa-locale": "trygg-hansa",
  "partners-trygg-hansa-boka-locale": "trygg-hansa",
  "pricing-locale": "pricing",
  "trust-center-locale": "security",
  "what-we-access-locale": "security",
  "download-app-locale": "download",
  "privacy-policy-locale": "privacy",
  "terms-and-conditions-locale": "terms",
  "company-locale": "company",
};

export function getSocialImage(
  routeName: keyof RouteNamedMap | undefined,
  locale: string,
): string | undefined {
  if (routeName === "blog-post-locale") {
    return undefined;
  }

  const image = (routeName && routeImages[routeName]) || "general";
  const language = locale === "sv" ? "sv" : "en";
  return images[`../assets/images/social/${image}-${language}.jpg`];
}

export function useSocialImage(
  route: RouteLocationNormalizedLoaded,
  locale: Ref<string>,
  siteUrl: string = import.meta.env.VITE_PUBLIC_SITE_URL,
): void {
  useHead(() => {
    const image = getSocialImage(route.name, locale.value);
    if (!image) {
      return { meta: [] };
    }

    const url = new URL(image, siteUrl).href;
    return {
      meta: [
        { property: "og:image", content: url },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:type", content: "image/jpeg" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: url },
      ],
    };
  });
}
